import type { AxiosRequestHeaders } from "axios";

import api from "@/lib/api";
import type { EntityAttachment } from "@/types/entity-attachment";
import { paymentService, type PaymentCreateResponse } from "@/services/payment.service";
import { getChargedAmount } from "@/utils/payment.utils";

export type TournamentRegistrationType = "TEAM" | "INDIVIDUAL" | "BOTH";
export type TournamentRegistrationStatus =
  | "PENDING_PAYMENT"
  | "CONFIRMED"
  | "CANCELLED"
  | "REFUNDED";

export interface TournamentRegistrationMember {
  id: string;
  userId: string;
  isCaptain: boolean;
  createdAt: string;
  user: {
    id: string;
    name: string | null;
    email: string | null;
    avatar?: string | null;
  };
}

export interface TournamentRosterInvitePreview {
  tournamentId: string;
  tournamentName: string;
  teamName: string;
  teamPhotoUrl: string | null;
  organizerTeamName: string;
  captainName: string;
  captainAvatar: string | null;
  memberCount: number;
}

export interface TournamentRosterInviteAcceptResult {
  members: TournamentRegistrationMember[];
  tournamentId: string;
  tournamentName: string;
  teamName: string | null;
  tournamentPath: string;
}

export interface TournamentRegistration {
  id: string;
  tournamentId: string;
  userId: string;
  tournamentTeamId: string | null;
  type: TournamentRegistrationType;
  status: TournamentRegistrationStatus;
  teamName: string | null;
  teamPhotoUrl?: string | null;
  paymentMethod: string | null;
  price: number | string;
  verificationCode: string;
  paymentExpiresAt: string | null;
  paidAt: string | null;
  createdAt: string;
  updatedAt: string;
  user?: {
    id: string;
    name: string | null;
    email: string | null;
  };
  tournament?: {
    id: string;
    shortCode?: string | null;
    teamId: string;
    name: string;
    status: string;
    sport?: string | null;
    category?: string | null;
    coverImage?: string | null;
    passFeesToUser?: boolean;
    startsAt?: string | null;
    venueName?: string | null;
    venueAddress?: string | null;
    team?: {
      id: string;
      slug?: string | null;
      name: string;
    };
  };
  team?: {
    id: string;
    name: string;
    seed: number | null;
  } | null;
  payments?: Array<{
    id: string;
    status: string;
    method: string;
    amount: number | string;
    providerPaymentId?: string | null;
  }>;
  members?: TournamentRegistrationMember[];
}

export interface TournamentRegistrationInfo {
  id: string;
  shortCode?: string | null;
  name: string;
  status: string;
  coverImage?: string | null;
  sport?: string;
  category?: string | null;
  classifyFormat?: string;
  classifyCount?: number;
  groupSize?: number | null;
  hasThirdPlace?: boolean;
  registrationType: TournamentRegistrationType;
  teamRegistrationPrice: number | string;
  individualRegistrationPrice: number | string;
  registrationOpenAt: string | null;
  registrationCloseAt: string | null;
  maxTeamRegistrations: number | null;
  maxIndividualRegistrations: number | null;
  playersPerTeam?: number | null;
  bothIndividualSlotLimit?: number | null;
  paymentTimeoutMinutes: number;
  passFeesToUser?: boolean;
  startsAt: string | null;
  prizeFirstPlace: number | string | null;
  prizeSecondPlace: number | string | null;
  prizeThirdPlace: number | string | null;
  venueName: string | null;
  venueAddress: string | null;
  venueNotes: string | null;
  attachments?: EntityAttachment[];
  team: {
    id: string;
    slug?: string | null;
    name: string;
  };
  teamRegistrationsCount?: number;
  individualRegistrationsCount?: number;
  _count?: {
    registrations: number;
    teams: number;
  };
}

/** O axios default usa `Content-Type: application/json`; com FormData o browser precisa definir o boundary. */
function formDataRequestConfig() {
  return {
    transformRequest: [
      (data: FormData, headers?: AxiosRequestHeaders) => {
        if (data instanceof FormData) {
          delete headers?.["Content-Type"];
        }
        return data;
      },
    ],
  };
}

export const tournamentRegistrationService = {
  async resolveByShortCode(shortCode: string): Promise<{ id: string; shortCode: string }> {
    const response = await api.get<{ id: string; shortCode: string }>(
      `/tournaments/by-short/${encodeURIComponent(shortCode)}`,
    );
    return response.data;
  },

  async getTournamentInfo(tournamentId: string): Promise<TournamentRegistrationInfo> {
    const response = await api.get<TournamentRegistrationInfo>(
      `/tournaments/${tournamentId}/registration-info`,
    );
    return response.data;
  },

  async registerTeam(
    tournamentId: string,
    data: { teamName: string; paymentMethod?: "PIX" | "CARD"; teamPhoto?: File },
  ): Promise<TournamentRegistration> {
    const fd = new FormData();
    fd.append("teamName", data.teamName.trim());
    fd.append("paymentMethod", data.paymentMethod ?? "PIX");
    if (data.teamPhoto) {
      fd.append("teamPhoto", data.teamPhoto);
    }
    const response = await api.post<TournamentRegistration>(
      `/tournaments/${tournamentId}/registrations/team`,
      fd,
      formDataRequestConfig(),
    );
    return response.data;
  },

  async registerIndividual(
    tournamentId: string,
    data: { paymentMethod?: "PIX" | "CARD" },
  ): Promise<TournamentRegistration> {
    const response = await api.post<TournamentRegistration>(
      `/tournaments/${tournamentId}/registrations/individual`,
      data,
    );
    return response.data;
  },

  async myRegistrations(): Promise<TournamentRegistration[]> {
    const response = await api.get<TournamentRegistration[]>("/tournaments/my-registrations");
    return response.data;
  },

  async myTournamentRegistrations(tournamentId: string): Promise<TournamentRegistration[]> {
    const response = await api.get<TournamentRegistration[]>(
      `/tournaments/${tournamentId}/registrations/me`,
    );
    return response.data;
  },

  async cancelMyRegistration(tournamentId: string, registrationId: string): Promise<void> {
    await api.delete(
      `/tournaments/${encodeURIComponent(tournamentId)}/registrations/${encodeURIComponent(registrationId)}`,
    );
  },

  async updatePendingTeamRegistration(
    tournamentId: string,
    registrationId: string,
    data: { teamName?: string; teamPhoto?: File },
  ): Promise<TournamentRegistration> {
    const fd = new FormData();
    if (data.teamName !== undefined) {
      fd.append("teamName", data.teamName.trim());
    }
    if (data.teamPhoto) {
      fd.append("teamPhoto", data.teamPhoto);
    }
    const response = await api.patch<TournamentRegistration>(
      `/tournaments/${encodeURIComponent(tournamentId)}/registrations/${encodeURIComponent(registrationId)}/team`,
      fd,
      formDataRequestConfig(),
    );
    return response.data;
  },

  async listRegistrationMembers(
    tournamentId: string,
    registrationId: string,
  ): Promise<TournamentRegistrationMember[]> {
    const response = await api.get<TournamentRegistrationMember[]>(
      `/tournaments/${encodeURIComponent(tournamentId)}/registrations/${encodeURIComponent(registrationId)}/members`,
    );
    return response.data;
  },

  async getRosterInviteLink(
    tournamentId: string,
    registrationId: string,
  ): Promise<string> {
    const response = await api.get<{ inviteLink: string }>(
      `/tournaments/${encodeURIComponent(tournamentId)}/registrations/${encodeURIComponent(registrationId)}/roster-invite-link`,
    );
    return response.data.inviteLink;
  },

  async regenerateRosterInviteLink(
    tournamentId: string,
    registrationId: string,
  ): Promise<string> {
    const response = await api.post<{ inviteLink: string }>(
      `/tournaments/${encodeURIComponent(tournamentId)}/registrations/${encodeURIComponent(registrationId)}/regenerate-roster-invite-link`,
    );
    return response.data.inviteLink;
  },

  async getRosterInvitePreview(token: string): Promise<TournamentRosterInvitePreview> {
    const response = await api.get<TournamentRosterInvitePreview>(
      `/tournaments/roster-invite/${encodeURIComponent(token)}/preview`,
    );
    return response.data;
  },

  async acceptRosterInvite(token: string): Promise<TournamentRosterInviteAcceptResult> {
    const response = await api.post<TournamentRosterInviteAcceptResult>(
      `/tournaments/roster-invite/${encodeURIComponent(token)}/accept`,
    );
    return response.data;
  },

  async removeRegistrationMember(
    tournamentId: string,
    registrationId: string,
    userId: string,
  ): Promise<TournamentRegistrationMember[]> {
    const response = await api.delete<TournamentRegistrationMember[]>(
      `/tournaments/${encodeURIComponent(tournamentId)}/registrations/${encodeURIComponent(registrationId)}/members/${encodeURIComponent(userId)}`,
    );
    return response.data;
  },

  async verifyPayment(code: string): Promise<TournamentRegistration> {
    const response = await api.get<TournamentRegistration>(`/tournaments/verify-payment/${code}`);
    return response.data;
  },

  async createPixPayment(registration: TournamentRegistration, payer: { name: string; email: string }) {
    const base = Number(registration.price);
    const passFeesToUser = registration.tournament?.passFeesToUser ?? true;
    const transaction_amount = getChargedAmount(base, "PIX", passFeesToUser);
    return paymentService.createPayment({
      transaction_amount,
      payment_method_id: "pix",
      description: `Inscrição torneio - ${registration.tournament?.name ?? registration.tournamentId}`,
      payer,
      tournamentId: registration.tournamentId,
      tournamentRegistrationId: registration.id,
      metadata: {
        type: "tournament_payment",
        tournamentId: registration.tournamentId,
        tournamentRegistrationId: registration.id,
      },
    }) as Promise<PaymentCreateResponse>;
  },

  async selfCheckIn(
    tournamentId: string,
    latitude: number,
    longitude: number,
  ): Promise<{
    success: boolean;
    message: string;
    checkedInAt: string;
  }> {
    const response = await api.post(`/tournaments/${tournamentId}/self-check-in`, {
      latitude,
      longitude,
    });
    return response.data;
  },
};
